import {
  type NormaliseNavData,
  type NormalizeNavDataInput,
  normalizeNavDataCore,
} from '@/modules/issuer-dashboard/helpers/normalizeNavDataCore.ts';
import { GetRegisterClassesWithNAVResponse } from 'api';

export type { NormaliseNavData } from '@/modules/issuer-dashboard/helpers/normalizeNavDataCore.ts';

type NormalizeNavWorkerResponse =
  | {
      requestId: number;
      data: NormaliseNavData[];
    }
  | {
      requestId: number;
      error: string;
    };

let normalizeNavWorker: Worker | null = null;
let normalizeNavRequestId = 0;
const pendingRequests = new Map<
  number,
  {
    resolve: (value: NormaliseNavData[]) => void;
    reject: (error: Error) => void;
  }
>();

const getNormalizeNavWorker = () => {
  if (typeof Worker === 'undefined') return null;

  normalizeNavWorker ??= new Worker(new URL('./normalizeNavData.worker.ts', import.meta.url), {
    type: 'module',
  });

  normalizeNavWorker.onmessage = (event: MessageEvent<NormalizeNavWorkerResponse>) => {
    const pendingRequest = pendingRequests.get(event.data.requestId);
    if (!pendingRequest) return;

    pendingRequests.delete(event.data.requestId);

    if ('error' in event.data) {
      pendingRequest.reject(new Error(event.data.error));
      return;
    }

    pendingRequest.resolve(event.data.data);
  };

  normalizeNavWorker.onerror = error => {
    pendingRequests.forEach(({ reject }) =>
      reject(error instanceof Error ? error : new Error('Failed to normalize NAV data')),
    );
    pendingRequests.clear();
  };

  return normalizeNavWorker;
};

export const normaliseNavData = (
  data?: GetRegisterClassesWithNAVResponse,
  withCurrentDate = true,
): NormaliseNavData[] => {
  return normalizeNavDataCore(data as NormalizeNavDataInput, withCurrentDate);
};

export const normaliseNavDataAsync = (
  data?: GetRegisterClassesWithNAVResponse,
  withCurrentDate = true,
) =>
  new Promise<NormaliseNavData[]>((resolve, reject) => {
    const worker = getNormalizeNavWorker();

    if (!worker) {
      resolve(normalizeNavDataCore(data as NormalizeNavDataInput, withCurrentDate));
      return;
    }

    const requestId = normalizeNavRequestId + 1;
    normalizeNavRequestId = requestId;

    pendingRequests.set(requestId, { resolve, reject });

    try {
      worker.postMessage({
        requestId,
        data: data as NormalizeNavDataInput,
        withCurrentDate,
      });
    } catch (error) {
      pendingRequests.delete(requestId);
      reject(error instanceof Error ? error : new Error('Failed to normalize NAV data'));
    }
  });
